import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';
import { createChart, AVAILABLE_TEMPLATES, MONITORING_FREQUENCIES } from './api';

export function ParticipantChartCreateModal({ open, onOpenChange, participantId, onCreated }) {
  const [flags, setFlags] = useState({});
  const [templateCode, setTemplateCode] = useState('bowel_chart');
  const [name, setName] = useState('');
  const [nameEdited, setNameEdited] = useState(false);
  const [frequency, setFrequency] = useState('daily');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Fetch user-visible flags once when the modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.get('/production/feature-flags/mine')
      .then(r => { if (!cancelled) setFlags(r?.data?.data || {}); })
      .catch(() => { if (!cancelled) setFlags({}); });
    return () => { cancelled = true; };
  }, [open]);

  const templates = useMemo(() => {
    return AVAILABLE_TEMPLATES.filter(t => {
      if (t.flag && !flags[t.flag]) return false;
      if (Array.isArray(t.requires)) {
        for (const req of t.requires) {
          if (!flags[req]) return false;
        }
      }
      return true;
    });
  }, [flags]);

  // Ensure the current selected template is still available after flags load
  useEffect(() => {
    if (templates.length === 0) return;
    if (!templates.some(t => t.code === templateCode)) setTemplateCode(templates[0].code);
  }, [templates, templateCode]);

  // Auto-fill the chart name from the selected template, unless staff typed their own.
  useEffect(() => {
    if (nameEdited) return;
    const selected = templates.find(t => t.code === templateCode);
    if (selected) setName(selected.name);
  }, [templateCode, templates, nameEdited]);

  const reset = () => {
    setTemplateCode(templates[0]?.code || 'bowel_chart');
    setName('');
    setNameEdited(false);
    setFrequency('daily');
    setNotes('');
  };

  const handleSubmit = async () => {
    if (!templateCode) { toast.error('Select a template'); return; }
    try {
      setSaving(true);
      const selected = templates.find(t => t.code === templateCode);
      const chart = await createChart(participantId, {
        template_code: templateCode,
        name: (name || selected?.name || 'Chart').trim(),
        monitoring_frequency: frequency || null,
        notes: notes || '',
      });
      toast.success(`Chart "${chart.name}" created`);
      reset();
      onOpenChange(false);
      onCreated?.(chart);
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e?.message || 'Failed to create chart';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="chart-create-dialog">
        <DialogHeader>
          <DialogTitle>Create chart</DialogTitle>
          <DialogDescription>
            Start a new clinical chart for this participant. Additional entry types open up as other chart modules are released.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="chart-template" className="text-xs">Template *</Label>
            <Select value={templateCode} onValueChange={setTemplateCode}>
              <SelectTrigger id="chart-template" data-testid="chart-template-select" className="mt-1">
                <SelectValue placeholder="Choose a template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => (
                  <SelectItem key={t.code} value={t.code} data-testid={`chart-template-opt-${t.code}`}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="chart-name" className="text-xs">Chart name</Label>
            <Input
              id="chart-name"
              value={name}
              onChange={e => { setName(e.target.value); setNameEdited(true); }}
              placeholder="Defaults to template name"
              maxLength={120}
              className="mt-1"
              data-testid="chart-name-input"
            />
          </div>
          <div>
            <Label htmlFor="chart-frequency" className="text-xs">Monitoring frequency</Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger id="chart-frequency" data-testid="chart-frequency-select" className="mt-1">
                <SelectValue placeholder="Choose frequency" />
              </SelectTrigger>
              <SelectContent>
                {MONITORING_FREQUENCIES.map(f => (
                  <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="chart-notes" className="text-xs">Notes</Label>
            <Textarea
              id="chart-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Optional context for staff recording entries"
              maxLength={2000}
              rows={3}
              className="mt-1"
              data-testid="chart-notes-input"
            />
            <p className="text-[11px] text-muted-foreground mt-1">{notes.length}/2000</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="chart-create-cancel">Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving || !templateCode} data-testid="chart-create-submit">
            {saving ? 'Saving…' : 'Create chart'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
